import type {
  TenantConfig,
  TenantLanguage,
  TenantModule,
} from '@models';

/**
 * Last-known tenant branding, persisted in `localStorage` so that a COLD START
 * WITHOUT NETWORK can still decide which shell to render instead of dead-ending.
 *
 * Context (bug "arranque offline → pantalla de mantenimiento"): on a cold start
 * with no network and an empty persistent cache, `getDoc(tenants/{id})` rejects
 * with `unavailable` ("Failed to get document because the client is offline",
 * `@firebase/firestore` 4.9.0). Without a seed the app then has literally zero
 * information about the tenant — not even whether it is a federation (`/tabs`)
 * or a club (`/home`) — and can only guess the shell.
 *
 * IMPORTANT — a seed is a STARTING POINT, NEVER A TRUTH:
 *  - it is always overwritten by a successful server read;
 *  - it never puts `loadState$` in `'loaded'` (we have not talked to the
 *    server, see `BrandingService.loadAndApply`);
 *  - it is deleted as soon as the server confirms the tenant does not exist.
 */
export interface BrandingSeed {
  /** Schema version — a bump invalidates every previously stored seed. */
  v: number;
  type: 'federation' | 'club' | null;
  logoUrl: string | null;
  tenantName: string | null;
  phoneCountryCode: string;
  modules: TenantModule[];
  languages: TenantLanguage[];
  /** Epoch ms of the successful read this seed came from (diagnostics). */
  savedAt: number;
}

/** Current seed schema version. Bump it whenever `BrandingSeed` changes shape. */
export const BRANDING_SEED_VERSION = 1;

/**
 * How long a seed stays usable, in ms (30 days).
 *
 * The seed is only ever applied when the server could not be reached, and any
 * successful read overwrites it, so in practice it expires only on a device
 * that has not managed a single online start in a month. Past that point the
 * honest answer is "I do not know this tenant anymore": tenant type, active
 * modules and logo may all have changed, and booting on month-old data is worse
 * than booting degraded, which is a supported path (`degradedShell(null)`).
 *
 * This is also what makes `savedAt` a field somebody reads, rather than
 * decoration. A seed with an unknown/zero `savedAt` is treated as expired for
 * the same reason: we cannot vouch for its age.
 */
export const BRANDING_SEED_TTL_MS = 30 * 24 * 60 * 60 * 1_000;

/** Minimal `localStorage`-compatible surface, so this module stays testable. */
export interface BrandingSeedStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Storage key for a tenant's branding seed.
 *
 * BOTH the Firebase projectId AND the tenantId are part of the key, and that is
 * not decoration: the runtime dev/pro toggle (`resolveFirebaseConfig()`) points
 * the very same binary at two different Firebase projects, where the same
 * tenantId can have a different type, different modules or not exist at all.
 * Seeding branding read from the wrong project would be worse than not seeding.
 *
 * A missing projectId (Firebase not initialised) yields `null`: better no seed
 * than a seed that cannot be attributed to a project.
 */
export function brandingSeedKey(
  tenantId: string | null | undefined,
  projectId: string | null | undefined,
): string | null {
  if (!tenantId || !projectId) return null;
  return `sm.branding.v${BRANDING_SEED_VERSION}.${projectId}.${tenantId}`;
}

/** Projects a freshly read `TenantConfig` onto the persistable subset. */
export function toBrandingSeed(
  config: TenantConfig,
  now: number = Date.now(),
): BrandingSeed {
  return {
    v: BRANDING_SEED_VERSION,
    type: config.type ?? null,
    logoUrl: config.branding?.logoUrl ?? null,
    tenantName: config.name ?? null,
    phoneCountryCode: config.phoneCountryCode ?? '+34',
    modules: config.modules ?? [],
    languages: config.languages?.length ? config.languages : ['es'],
    savedAt: now,
  };
}

/**
 * Reads and validates a stored seed. Returns `null` for anything that is not a
 * well-formed seed of the current version (corrupt JSON, older schema, a value
 * written by something else..., or no usable storage at all). Never throws: a
 * broken seed must degrade to "no seed", never break the app boot.
 */
export function readBrandingSeed(
  storage: BrandingSeedStorage | null,
  key: string | null,
  now: number = Date.now(),
): BrandingSeed | null {
  if (!storage || !key) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null; // Safari private mode & friends
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<BrandingSeed> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.v !== BRANDING_SEED_VERSION) return null;
    const type = parsed.type;
    if (type !== 'federation' && type !== 'club' && type !== null) return null;

    // Expiry (see `BRANDING_SEED_TTL_MS`). A `savedAt` in the future — the user
    // moved the device clock back — is NOT treated as expired: punishing a
    // clock change with a degraded boot would be gratuitous, and the seed is
    // still the best thing we have.
    const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
    if (now - savedAt > BRANDING_SEED_TTL_MS) return null;

    return {
      v: BRANDING_SEED_VERSION,
      type,
      logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
      tenantName:
        typeof parsed.tenantName === 'string' ? parsed.tenantName : null,
      phoneCountryCode:
        typeof parsed.phoneCountryCode === 'string'
          ? parsed.phoneCountryCode
          : '+34',
      modules: Array.isArray(parsed.modules) ? parsed.modules : [],
      languages:
        Array.isArray(parsed.languages) && parsed.languages.length
          ? parsed.languages
          : ['es'],
      savedAt,
    };
  } catch {
    return null;
  }
}

/** Persists a seed. Never throws (quota exceeded / private mode). */
export function writeBrandingSeed(
  storage: BrandingSeedStorage | null,
  key: string | null,
  seed: BrandingSeed,
): void {
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify(seed));
  } catch {
    /* persisting branding is best-effort — never break the boot for it */
  }
}

/** Drops a seed (used when the server confirms the tenant does not exist). */
export function clearBrandingSeed(
  storage: BrandingSeedStorage | null,
  key: string | null,
): void {
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    /* noop */
  }
}

/**
 * `localStorage`, or `null` when it is not usable.
 *
 * NOT paranoia and NOT redundant with the `try/catch` inside every function of
 * this module: on an origin where storage is disabled (Android WebView with
 * site data blocked, Safari in some lockdown configurations, `data:`/`file:`
 * origins) merely EVALUATING the `localStorage` property throws `SecurityError`
 * — before any of those functions is entered. And the caller here is
 * `BrandingService.loadAndApply()`, a `provideAppInitializer`: a throw there
 * rejects the initializer, `bootstrapApplication()` lands in the `.catch()` of
 * `main.ts`, and the user gets the eternal splash screen this whole change
 * exists to remove.
 *
 * Everything that touches storage MUST come through here.
 */
export function safeLocalStorage(): BrandingSeedStorage | null {
  try {
    // The property access itself is the dangerous part.
    const storage = globalThis.localStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}
